import { compact, flatten, head, isEmpty, map, omit, uniq, without } from 'lodash';
import { normalize, schema as normalizrSchema } from 'normalizr';
import { all, put, select } from 'redux-saga/effects';
import { createAsyncAction } from 'typesafe-actions';

import { ResourceLite } from '../resources/createResource';
import { EntityBase, EntityID } from '../resources/types';

import fetchLookups from './fetchLookups';
import sagaApi, { Meta, PayloadApiFailure, PayloadApiRequest, PayloadApiSuccess } from './sagaApi';
import sagaHandleCached from './sagaHandleCached';

export interface FetchBulkPayload<E extends EntityBase> extends PayloadApiRequest<E> {
  resource: ResourceLite<E>;
  method?: 'get';
  ids: EntityID[];
  meta?: Meta<E> & { forceFetch?: boolean };
}

const fetchBulk = createAsyncAction(
  '@lib/fetchBulk/REQUEST',
  '@lib/fetchBulk/SUCCESS',
  '@lib/fetchBulk/FAILURE',
  '@lib/fetchBulk/CANCEL',
)<FetchBulkPayload<any>, PayloadApiSuccess<any>, PayloadApiFailure, any>();

// saga
export function* sagaFetchBulk(action: ReturnType<typeof fetchBulk.request>) {
  const {
    payload: { ids, ...payload },
  } = action;
  const { resource, meta = {} } = payload;
  const { forceFetch, ...restMeta } = meta;

  let idsToFetch = compact(uniq(flatten(ids)));
  if (!forceFetch) {
    // @ts-ignore
    const entitiesCached = yield all(map(idsToFetch, (id) => select(resource.makeSelectEntity(id))));
    const idsCached = compact(uniq(map(entitiesCached, 'id')));
    idsToFetch = without(idsToFetch, ...idsCached);
    if (isEmpty(idsToFetch)) {
      // @ts-ignore
      return yield sagaHandleCached({
        action: fetchBulk,
        result: idsCached,
        data: entitiesCached,
        payload,
      });
    }
  }

  // Run one single call in case the resource supports multiple IDs at once
  if (resource.fetchBulkUrl && idsToFetch.length > 1) {
    const response: PayloadApiSuccess<any> = yield sagaApi({
      url: resource.fetchBulkUrl,
      params: {
        _id: idsToFetch,
        // TODO: Enable `includeDeleted` on bulk once API supports it
        // includeDeleted: true,
      },
      ...payload,
      meta: {
        schema: resource.fetchBulkSchema,
        ignoreErrors: true,
        ...restMeta,
      },
    });
    // @ts-ignore
    return yield put(fetchBulk.success(response));
  }

  // Run multiple calls in parallel, one per ID
  const responses: PayloadApiSuccess<any>[] = yield all(
    map(idsToFetch, (id) =>
      sagaApi({
        url: `${resource.url}/${id}`,
        params: {
          includeDeleted: true,
        },
        ...payload,
        meta: {
          schema: resource.fetchSchema,
          ignoreErrors: true,
          ...omit(restMeta, ['includeLookups', 'resolveLookups']),
        },
      }),
    ),
  );
  const data = map(responses, 'data');
  const normalized = normalize(data, new normalizrSchema.Array(resource.fetchSchema));

  // Fetch lookups recursively
  if (restMeta.includeLookups) {
    yield put(fetchLookups({ resource, normalized, meta: restMeta }));
  }

  // Dispatching action success with all normalized data at once to improve performance
  const firstResponse = head(responses) as PayloadApiSuccess<any>;
  yield put(fetchBulk.success({ ...firstResponse, data, normalized, meta }));
}

export default fetchBulk;
